// Email generation helpers extracted from IncidentUpdates.tsx

import incidentTemplate from '@/components/IncidentUpdates/incident_update.html?raw';
import logoSvg from './CBRE_green.svg?raw'; // inline SVG raw import

export interface Incident {
  id: string;
  title: string;
  // rename: status -> state (matches pages and API)
  state: string;
  severity: 'P4' | 'P3' | 'P2' | 'P1';
  updatedAt: string;
  owner?: string;
  priority?: string;
  description?: string;
  businessImpact?: string;
  bridgeDetails?: string;
  businessLine?: string;
  startTime?: string;
  endTime?: string;
  problemRecord?: string;
  circumvention_time?: string; // ✅ Add this field
}

export interface Communication {
  id: string;
  incidentId: string;
  author: string;
  role: string;
  timestamp: string;
  content: string;
  iscircumvented?: boolean;
  iscommunication_sent?: boolean;
  approved?: boolean;
  approval_id?: number; 
  update_id?: number;
}

export const fmtDT = (iso?: string) => {
  if (!iso) return 'N/A';
  try {
    const d = new Date(iso);
    return d
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', '');
  } catch {
    return iso;
  }
};

// Build Updates block (only include approved communications)
export const buildUpdatesBlock = (
  incident: Incident,
  comms: Communication[],
  closingNotes?: string
) => {
  // Only approved comms
  const approvedComms = comms.filter(c => c.approved);

  // Auto derive closingNotes from approved circumvented comms if not explicitly passed
  let effectiveClosing = closingNotes;
  if (!effectiveClosing) {
    const circumventedComms = approvedComms.filter(c => c.iscircumvented);
    if (circumventedComms.length) {
      const merged = circumventedComms
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map(c => c.content.trim())
        .filter(Boolean)
        .join('\n');
      effectiveClosing = merged || undefined;
    }
  }

  // Exclude circumvented (approved) updates from normal list if used as closing notes
  const normalComms = approvedComms.filter(c => !c.iscircumvented);

  const asc = [...normalComms].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  const labeled = asc.map((c, idx) => ({
    comm: c,
    label: idx === 0 ? 'Initial Update' : `Update ${idx}`
  }));
  const desc = labeled.slice().reverse();

  const updateItems = desc
    .map(
      ({ comm, label }, i) => `
        ${i === 0 ? '' : '<div style="height:8px"></div>'}
        <div>
          <strong>${label}:</strong>
          <ul>${comm.content
            .split(/\n+/)
            .map(l => l.trim())
            .filter(Boolean)
            .map(l => `<li>${l.replace(/^-+\s*/, '')}</li>`)
            .join('')}</ul>
        </div>`
    )
    .join('');

  const cleanedClosing = (effectiveClosing || '').trim();
  let closingItem = '';
  if (cleanedClosing) {
    const lines = cleanedClosing
      .split(/\n+/)
      .map(l => l.trim())
      .filter(Boolean)
      .map(l => `<li>${l.replace(/^-+\s*/, '')}</li>`)
      .join('');
    closingItem = `
      <div>
        <strong>Circumvented:</strong>
        <ul>${lines}</ul>
      </div>
      ${updateItems ? '<div style="height:24px"></div>' : ''}
    `;
  }

  const headerTitle = cleanedClosing ? 'Circumvented:' : 'Updates';

  const finalContent =
    cleanedClosing || updateItems
      ? `${closingItem}${!cleanedClosing ? updateItems : updateItems}`
      : '<div><em>No approved updates yet.</em></div>';

  return `
    <div class="sectionHeader">${headerTitle}</div>
    <div class="panel">
      ${finalContent}
    </div>
  `;
};

// Main builder (returns full HTML string)
export const buildIncidentEmailHTML = (
  incident: Incident,
  impacted: string[],
  comms: Communication[],
  execSummary: string,
  closingNotes?: string,
  forPreview = false
) => {
  const priority =
    incident.priority ||
    (incident.severity === 'P4'
      ? 'PP1'
      : incident.severity === 'P3'
      ? 'PP2'
      : 'PP3');

  const safeExec =
    execSummary?.trim() ||
    'Executive summary is being prepared. Further details will follow.';

  // ✅ Use base64 data URI for preview, CID placeholder for email
  const logoBlock = forPreview
    ? `<img alt="CBRE" height="40" style="display:block" src="data:image/svg+xml;base64,${btoa(logoSvg)}" />`
    : `<img alt="CBRE" height="40" style="display:block" src="cid:cbrelogo" />`;
  
  // ✅ Use circumvention_time if available, otherwise fall back to endTime
  const circumventionTime = incident.circumvention_time 
    ? fmtDT(incident.circumvention_time)
    : (incident.endTime ? fmtDT(incident.endTime) : 'N/A');
  
  let inc_state = incident.state;
  if (closingNotes && closingNotes.length > 0) {
     inc_state = 'Circumvented';
  } 
    
  return incidentTemplate
    .replaceAll('{{TITLE}}', incident.title || 'Incident Update')
    .replaceAll('{{LOGO_BLOCK}}', logoBlock || 'CBRE')
    .replaceAll('{{INCIDENT_NUMBER}}', incident.id)
    .replaceAll('{{PRIORITY}}', priority)
    .replaceAll('{{STATUS}}', (inc_state || 'open').replace(/^\w/, m => m.toUpperCase()))
    .replaceAll('{{START_TIME}}', fmtDT(incident.startTime || incident.updatedAt))
    .replaceAll('{{END_TIME}}', circumventionTime)
    .replaceAll('{{APPLICATIONS_IMPACTED}}', impacted.join(', ') || 'TBD')
    .replaceAll('{{DESCRIPTION}}', incident.description || incident.title || '-')
    .replaceAll('{{BUSINESS_IMPACT}}', incident.businessImpact || '-')
    .replaceAll('{{BRIDGE_DETAILS}}', incident.bridgeDetails || '-')
    .replaceAll('{{BUSINESS_LINE}}', incident.businessLine || '-')
    .replaceAll('{{PROBLEM_RECORD}}', incident.problemRecord || 'TBD')
    .replaceAll('{{RCA_OWNER}}', incident.owner || 'TBD')
    .replaceAll('{{EXEC_SUMMARY}}', safeExec.replace(/\n/g, '<br/>'))
    .replaceAll('{{UPDATES_BLOCK}}', buildUpdatesBlock(incident, comms, closingNotes))
    .replaceAll('{{FOOTER_NOTE}}', 'Please reach out to !Managed Services L1.5 ManagedServicesL1.5@cbre.com for any queries.');
};